import React, { useMemo, useState, useCallback, useEffect } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  MarkerType,
  NodeProps,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';
import type { SimWorld } from '../../simulation/types/SimWorld';
import type { SimNPC } from '../../simulation/types/SimNPC';

interface FamilyTreeGraphPanelProps {
  world: SimWorld;
  onNPCClick?: (npc: SimNPC) => void;
  isMobile?: boolean;
}

// カスタムノードのデータ型
interface NPCNodeData {
  npc: SimNPC;
  isSelected: boolean;
  onClick?: (npc: SimNPC) => void;
}

// カスタムNPCノードコンポーネント
const NPCNode: React.FC<NodeProps<NPCNodeData>> = ({ data }) => {
  const { npc, isSelected, onClick } = data;
  const winRate = npc.wins + npc.losses > 0
    ? Math.round((npc.wins / (npc.wins + npc.losses)) * 100)
    : 0;

  const borderColor = isSelected
    ? '#ffd700'
    : npc.isAlive
      ? '#4ade80'
      : '#ef4444';

  return (
    <div
      style={{
        padding: '8px 12px',
        backgroundColor: isSelected ? '#2a2a5e' : '#1e1e3f',
        border: `2px solid ${borderColor}`,
        borderRadius: '8px',
        cursor: 'pointer',
        minWidth: '100px',
        textAlign: 'center',
        boxShadow: isSelected ? '0 0 10px #ffd700' : 'none',
      }}
      onClick={() => onClick?.(npc)}
    >
      {/* 上部ハンドル（親からの接続） */}
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: '#555', width: 8, height: 8 }}
      />

      {/* 名前 */}
      <div style={{ color: '#fff', fontWeight: 'bold', fontSize: '12px', marginBottom: '4px' }}>
        {npc.name}
        {!npc.isAlive && ' 💀'}
      </div>

      {/* 情報行 */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', fontSize: '10px' }}>
        <span style={{ color: '#888' }}>G{npc.generation}</span>
        <span style={{ color: '#888' }}>{npc.age}歳</span>
        <span
          style={{
            color: winRate >= 50 ? '#4ade80' : winRate >= 30 ? '#facc15' : '#ef4444',
            fontWeight: 'bold',
          }}
        >
          {winRate}%
        </span>
      </div>

      {/* 下部ハンドル（子への接続） */}
      <Handle
        type="source"
        position={Position.Bottom}
        style={{ background: '#555', width: 8, height: 8 }}
      />

      {/* パートナー接続用の左右ハンドル */}
      <Handle
        type="source"
        position={Position.Right}
        id="partner-out"
        style={{ background: '#e879f9', width: 6, height: 6, top: '50%' }}
      />
      <Handle
        type="target"
        position={Position.Left}
        id="partner-in"
        style={{ background: '#e879f9', width: 6, height: 6, top: '50%' }}
      />
    </div>
  );
};

// ノードタイプ定義
const nodeTypes = {
  npcNode: NPCNode,
};

/**
 * 直系フィルタリング：選択NPCの先祖・子孫を取得
 */
function getDirectLineage(
  selectedNpcId: string,
  npcs: SimNPC[],
  generations: number = 3
): Set<string> {
  const npcMap = new Map<string, SimNPC>();
  npcs.forEach(npc => npcMap.set(npc.id, npc));

  const lineageSet = new Set<string>();
  lineageSet.add(selectedNpcId);

  const selectedNpc = npcMap.get(selectedNpcId);
  if (!selectedNpc) return lineageSet;

  // 先祖を辿る（上方向）
  const getAncestors = (npcId: string, depth: number) => {
    if (depth <= 0) return;
    const npc = npcMap.get(npcId);
    if (!npc) return;

    const parents = npc.family.filter(f => f.relationType === 'parent');
    for (const parentRel of parents) {
      lineageSet.add(parentRel.relationId);
      getAncestors(parentRel.relationId, depth - 1);
    }
  };

  // 子孫を辿る（下方向）
  const getDescendants = (npcId: string, depth: number) => {
    if (depth <= 0) return;
    const npc = npcMap.get(npcId);
    if (!npc) return;

    const children = npc.family.filter(f => f.relationType === 'child');
    for (const childRel of children) {
      lineageSet.add(childRel.relationId);
      getDescendants(childRel.relationId, depth - 1);
    }
  };

  // パートナーも含める
  const partner = selectedNpc.family.find(f => f.relationType === 'partner');
  if (partner) {
    lineageSet.add(partner.relationId);
  }

  getAncestors(selectedNpcId, generations);
  getDescendants(selectedNpcId, generations);

  // 含まれているNPCのパートナーも追加
  const lineageArray = Array.from(lineageSet);
  for (const npcId of lineageArray) {
    const npc = npcMap.get(npcId);
    if (npc) {
      const partnerRel = npc.family.find(f => f.relationType === 'partner');
      if (partnerRel) {
        lineageSet.add(partnerRel.relationId);
      }
    }
  }

  return lineageSet;
}

/**
 * 家系図レイアウトモード
 */
type LayoutMode = 'generation' | 'family';

/**
 * 家族単位（カップル＋子供）の構造
 */
interface FamilyUnit {
  id: string;
  primary: SimNPC;
  partner: SimNPC | null;
  children: SimNPC[];
  x: number;
  y: number;
  width: number;
}

/**
 * 家系図レイアウトでノードとエッジを生成
 */
function generateFamilyLayout(
  npcs: SimNPC[],
  selectedNpcId: string | null,
  onNPCClick?: (npc: SimNPC) => void
): { nodes: Node<NPCNodeData>[]; edges: Edge[] } {
  const npcMap = new Map<string, SimNPC>();
  npcs.forEach(npc => npcMap.set(npc.id, npc));

  const nodeYSpacing = 150;
  const nodeXSpacing = 140;
  const coupleGap = 30; // パートナー間の間隔

  // 配置済みNPCを追跡
  const positionedNpcs = new Map<string, { x: number; y: number }>();
  const processedAsPartner = new Set<string>();

  // パートナーを取得
  const getPartner = (npc: SimNPC): SimNPC | null => {
    const rel = npc.family.find(f => f.relationType === 'partner');
    return rel ? npcMap.get(rel.relationId) || null : null;
  };

  // 子供を取得
  const getChildren = (npc: SimNPC): SimNPC[] => {
    return npc.family
      .filter(f => f.relationType === 'child')
      .map(f => npcMap.get(f.relationId))
      .filter((c): c is SimNPC => c !== undefined);
  };

  // 親を取得
  const getParents = (npc: SimNPC): SimNPC[] => {
    return npc.family
      .filter(f => f.relationType === 'parent')
      .map(f => npcMap.get(f.relationId))
      .filter((p): p is SimNPC => p !== undefined);
  };

  // 初代（親がいないNPC）を取得
  const founders = npcs.filter(npc => {
    const parents = getParents(npc);
    return parents.length === 0;
  });

  // 家族単位を構築して幅を計算
  const buildFamilyUnit = (npc: SimNPC): FamilyUnit => {
    const partner = getPartner(npc);
    const children = getChildren(npc);

    // パートナーの子供も統合（重複除去）
    if (partner) {
      const partnerChildren = getChildren(partner);
      for (const pc of partnerChildren) {
        if (!children.find(c => c.id === pc.id)) {
          children.push(pc);
        }
      }
    }

    // 子供を年齢順（生まれた順）にソート
    children.sort((a, b) => b.age - a.age);

    const coupleWidth = partner ? nodeXSpacing + coupleGap : nodeXSpacing;
    const childrenWidth = children.length * nodeXSpacing;
    const width = Math.max(coupleWidth, childrenWidth);

    return {
      id: partner ? `${npc.id}_${partner.id}` : npc.id,
      primary: npc,
      partner,
      children,
      x: 0,
      y: 0,
      width,
    };
  };

  // 再帰的にレイアウト
  const layoutFamily = (
    npc: SimNPC,
    startX: number,
    depth: number
  ): number => {
    // 既に配置済みならスキップ
    if (positionedNpcs.has(npc.id) || processedAsPartner.has(npc.id)) {
      return startX;
    }

    const partner = getPartner(npc);
    const unit = buildFamilyUnit(npc);

    // 子供を先に再帰配置して幅を確定
    let childrenTotalWidth = 0;
    const childPositions: { npc: SimNPC; x: number }[] = [];

    let childX = startX;
    for (const child of unit.children) {
      if (!positionedNpcs.has(child.id) && !processedAsPartner.has(child.id)) {
        const childEndX = layoutFamily(child, childX, depth + 1);
        const childPos = positionedNpcs.get(child.id);
        if (childPos) {
          childPositions.push({ npc: child, x: childPos.x });
        }
        childX = childEndX + nodeXSpacing;
      }
    }

    if (childPositions.length > 0) {
      childrenTotalWidth = childX - startX - nodeXSpacing;
    }

    // 親の位置を子供の中央に配置
    let parentX: number;
    if (childPositions.length > 0) {
      const firstChildX = childPositions[0].x;
      const lastChildX = childPositions[childPositions.length - 1].x;
      parentX = (firstChildX + lastChildX) / 2;
      if (partner) {
        parentX -= coupleGap / 2; // カップルの中央を合わせる
      }
    } else {
      parentX = startX;
    }

    const y = depth * nodeYSpacing;

    // メインNPCを配置
    positionedNpcs.set(npc.id, { x: parentX, y });

    // パートナーを配置
    if (partner && !positionedNpcs.has(partner.id)) {
      processedAsPartner.add(partner.id);
      positionedNpcs.set(partner.id, { x: parentX + nodeXSpacing + coupleGap, y });
    }

    // 使用した幅を返す
    return Math.max(
      parentX + (partner ? nodeXSpacing + coupleGap : 0),
      childX > startX ? childX - nodeXSpacing : startX
    );
  };

  // 初代から配置開始
  let currentX = 0;
  for (const founder of founders) {
    if (!positionedNpcs.has(founder.id) && !processedAsPartner.has(founder.id)) {
      currentX = layoutFamily(founder, currentX, 0) + nodeXSpacing * 2;
    }
  }

  // 孤立NPC（親も子もパートナーもいない）を配置
  for (const npc of npcs) {
    if (!positionedNpcs.has(npc.id)) {
      positionedNpcs.set(npc.id, { x: currentX, y: npc.generation * nodeYSpacing });
      currentX += nodeXSpacing;
    }
  }

  // ノード生成
  const nodes: Node<NPCNodeData>[] = [];
  for (const npc of npcs) {
    const pos = positionedNpcs.get(npc.id);
    if (pos) {
      nodes.push({
        id: npc.id,
        type: 'npcNode',
        position: { x: pos.x, y: pos.y },
        data: {
          npc,
          isSelected: npc.id === selectedNpcId,
          onClick: onNPCClick,
        },
      });
    }
  }

  // エッジ生成
  const edges: Edge[] = [];
  const processedPairs = new Set<string>();

  for (const npc of npcs) {
    // 親子関係（片親のみ）
    const parents = getParents(npc);
    if (parents.length > 0 && npcMap.has(parents[0].id)) {
      edges.push({
        id: `parent-${parents[0].id}-${npc.id}`,
        source: parents[0].id,
        target: npc.id,
        type: 'smoothstep',
        style: { stroke: '#666', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#666' },
      });
    }

    // パートナー関係
    const partner = getPartner(npc);
    if (partner && npcMap.has(partner.id)) {
      const pairKey = [npc.id, partner.id].sort().join('-');
      if (!processedPairs.has(pairKey)) {
        processedPairs.add(pairKey);
        edges.push({
          id: `partner-${pairKey}`,
          source: npc.id,
          target: partner.id,
          sourceHandle: 'partner-out',
          targetHandle: 'partner-in',
          type: 'straight',
          style: { stroke: '#e879f9', strokeWidth: 2, strokeDasharray: '5,5' },
          animated: true,
        });
      }
    }
  }

  return { nodes, edges };
}

/**
 * 世代順レイアウトでノードとエッジを生成
 */
function generateGenerationLayout(
  npcs: SimNPC[],
  selectedNpcId: string | null,
  onNPCClick?: (npc: SimNPC) => void
): { nodes: Node<NPCNodeData>[]; edges: Edge[] } {
  const npcMap = new Map<string, SimNPC>();
  npcs.forEach(npc => npcMap.set(npc.id, npc));

  const nodeYSpacing = 150;
  const nodeXSpacing = 180;

  // 世代ごとにグループ化
  const generationGroups = new Map<number, SimNPC[]>();
  for (const npc of npcs) {
    const gen = npc.generation;
    if (!generationGroups.has(gen)) {
      generationGroups.set(gen, []);
    }
    generationGroups.get(gen)!.push(npc);
  }

  const nodes: Node<NPCNodeData>[] = [];
  const generations = Array.from(generationGroups.keys()).sort((a, b) => a - b);

  for (const gen of generations) {
    const genNpcs = generationGroups.get(gen)!;
    const startX = -(genNpcs.length - 1) * nodeXSpacing / 2;

    genNpcs.forEach((npc, index) => {
      nodes.push({
        id: npc.id,
        type: 'npcNode',
        position: { x: startX + index * nodeXSpacing, y: gen * nodeYSpacing },
        data: {
          npc,
          isSelected: npc.id === selectedNpcId,
          onClick: onNPCClick,
        },
      });
    });
  }

  // エッジ生成
  const edges: Edge[] = [];
  const processedPairs = new Set<string>();

  for (const npc of npcs) {
    const parents = npc.family.filter(f => f.relationType === 'parent');
    for (const parentRel of parents) {
      if (npcMap.has(parentRel.relationId)) {
        edges.push({
          id: `parent-${parentRel.relationId}-${npc.id}`,
          source: parentRel.relationId,
          target: npc.id,
          type: 'smoothstep',
          style: { stroke: '#666', strokeWidth: 2 },
          markerEnd: { type: MarkerType.ArrowClosed, color: '#666' },
        });
      }
    }

    const partnerRel = npc.family.find(f => f.relationType === 'partner');
    if (partnerRel && npcMap.has(partnerRel.relationId)) {
      const pairKey = [npc.id, partnerRel.relationId].sort().join('-');
      if (!processedPairs.has(pairKey)) {
        processedPairs.add(pairKey);
        edges.push({
          id: `partner-${pairKey}`,
          source: npc.id,
          target: partnerRel.relationId,
          sourceHandle: 'partner-out',
          targetHandle: 'partner-in',
          type: 'straight',
          style: { stroke: '#e879f9', strokeWidth: 2, strokeDasharray: '5,5' },
          animated: true,
        });
      }
    }
  }

  return { nodes, edges };
}

/**
 * NPCリストからノードとエッジを生成
 */
function generateGraph(
  npcs: SimNPC[],
  selectedNpcId: string | null,
  onNPCClick?: (npc: SimNPC) => void,
  filterLineage: boolean = false,
  lineageDepth: number = 3,
  layoutMode: LayoutMode = 'family'
): { nodes: Node<NPCNodeData>[]; edges: Edge[] } {
  // フィルタリング
  let filteredNpcs = npcs;
  if (filterLineage && selectedNpcId) {
    const lineageSet = getDirectLineage(selectedNpcId, npcs, lineageDepth);
    filteredNpcs = npcs.filter(npc => lineageSet.has(npc.id));
  }

  if (layoutMode === 'family') {
    return generateFamilyLayout(filteredNpcs, selectedNpcId, onNPCClick);
  } else {
    return generateGenerationLayout(filteredNpcs, selectedNpcId, onNPCClick);
  }
}

/**
 * グラフィカル家系図パネル
 */
export const FamilyTreeGraphPanel: React.FC<FamilyTreeGraphPanelProps> = ({
  world,
  onNPCClick,
  isMobile = false,
}) => {
  const [selectedNpcId, setSelectedNpcId] = useState<string | null>(null);
  const [filterLineage, setFilterLineage] = useState(false);
  const [lineageDepth, setLineageDepth] = useState(3);
  const [layoutMode, setLayoutMode] = useState<LayoutMode>('family');

  // 内部クリックハンドラ
  const handleNPCClick = useCallback((npc: SimNPC) => {
    setSelectedNpcId(npc.id);
    onNPCClick?.(npc);
  }, [onNPCClick]);

  // グラフ生成
  const { initialNodes, initialEdges } = useMemo(() => {
    const { nodes, edges } = generateGraph(
      world.npcs,
      selectedNpcId,
      handleNPCClick,
      filterLineage,
      lineageDepth,
      layoutMode
    );
    return { initialNodes: nodes, initialEdges: edges };
  }, [world.npcs, selectedNpcId, handleNPCClick, filterLineage, lineageDepth, layoutMode]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // world.npcsやフィルタ設定が変わったらノード/エッジを更新
  useEffect(() => {
    setNodes(initialNodes);
    setEdges(initialEdges);
  }, [initialNodes, initialEdges, setNodes, setEdges]);

  // NPC選択リセット
  const clearSelection = useCallback(() => {
    setSelectedNpcId(null);
  }, []);

  // 統計
  const totalNpcs = world.npcs.length;
  const displayedNpcs = nodes.length;

  return (
    <div style={styles.container}>
      {/* ヘッダー */}
      <div style={styles.header}>
        <h3 style={styles.title}>🌳 家系図（グラフ）</h3>
        <div style={styles.controls}>
          {/* レイアウトモード */}
          <select
            value={layoutMode}
            onChange={(e) => setLayoutMode(e.target.value as LayoutMode)}
            style={styles.select}
          >
            <option value="family">家系図配置</option>
            <option value="generation">世代順配置</option>
          </select>

          {/* フィルタトグル */}
          <label style={styles.filterLabel}>
            <input
              type="checkbox"
              checked={filterLineage}
              onChange={(e) => setFilterLineage(e.target.checked)}
              style={styles.checkbox}
            />
            直系のみ表示
          </label>

          {/* 世代数選択 */}
          {filterLineage && (
            <select
              value={lineageDepth}
              onChange={(e) => setLineageDepth(Number(e.target.value))}
              style={styles.select}
            >
              <option value={1}>±1世代</option>
              <option value={2}>±2世代</option>
              <option value={3}>±3世代</option>
              <option value={5}>±5世代</option>
              <option value={10}>±10世代</option>
            </select>
          )}

          {/* 選択クリア */}
          {selectedNpcId && (
            <button onClick={clearSelection} style={styles.clearButton}>
              選択解除
            </button>
          )}
        </div>
      </div>

      {/* 情報バー */}
      <div style={styles.infoBar}>
        <span>表示: {displayedNpcs} / {totalNpcs}人</span>
        {selectedNpcId && (
          <span style={{ marginLeft: '16px' }}>
            選択中: {world.npcs.find(n => n.id === selectedNpcId)?.name || '不明'}
          </span>
        )}
      </div>

      {/* グラフ */}
      <div style={styles.graphContainer}>
        {displayedNpcs === 0 ? (
          <div style={styles.emptyMessage}>
            {filterLineage
              ? '表示するNPCがありません。NPCを選択するか、直系のみ表示を解除してください。'
              : 'NPCがいません。'
            }
          </div>
        ) : (
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            minZoom={0.1}
            maxZoom={2}
            defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
            attributionPosition="bottom-left"
            style={{ background: '#0f0f1e' }}
          >
            <Background color="#333" gap={32} />
            <Controls
              style={{ background: '#1e1e3f', border: '1px solid #444' }}
            />
            <MiniMap
              nodeColor={(node) => {
                const npc = (node.data as NPCNodeData).npc;
                return npc.isAlive ? '#4ade80' : '#ef4444';
              }}
              style={{ background: '#1a1a2e', border: '1px solid #444' }}
              maskColor="rgba(0, 0, 0, 0.7)"
            />
          </ReactFlow>
        )}
      </div>

      {/* 凡例 */}
      <div style={styles.footer}>
        <span style={styles.legend}>
          <span style={{ ...styles.legendItem, color: '#4ade80' }}>● 生存</span>
          <span style={{ ...styles.legendItem, color: '#ef4444' }}>● 死亡</span>
          <span style={{ ...styles.legendItem, color: '#ffd700' }}>● 選択中</span>
          <span style={{ ...styles.legendItem, color: '#e879f9' }}>--- パートナー</span>
          <span style={{ ...styles.legendItem, color: '#666' }}>→ 親子</span>
        </span>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#1a1a2e',
    border: '2px solid #444',
    borderRadius: '8px',
    height: '600px',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    backgroundColor: '#0f0f1e',
    borderBottom: '1px solid #444',
  },
  title: {
    margin: 0,
    color: '#fff',
    fontSize: '18px',
    fontWeight: 'bold',
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  filterLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    color: '#aaa',
    fontSize: '13px',
    cursor: 'pointer',
  },
  checkbox: {
    cursor: 'pointer',
  },
  select: {
    padding: '4px 8px',
    backgroundColor: '#2a2a3e',
    border: '1px solid #444',
    borderRadius: '4px',
    color: '#ddd',
    fontSize: '12px',
    cursor: 'pointer',
  },
  clearButton: {
    padding: '4px 12px',
    backgroundColor: '#3a3a5e',
    border: '1px solid #555',
    borderRadius: '4px',
    color: '#ddd',
    fontSize: '12px',
    cursor: 'pointer',
  },
  infoBar: {
    padding: '8px 16px',
    backgroundColor: '#15152e',
    borderBottom: '1px solid #333',
    color: '#888',
    fontSize: '12px',
  },
  graphContainer: {
    flex: 1,
    overflow: 'hidden',
  },
  emptyMessage: {
    color: '#666',
    fontSize: '14px',
    textAlign: 'center',
    padding: '60px 20px',
  },
  footer: {
    padding: '8px 16px',
    backgroundColor: '#0f0f1e',
    borderTop: '1px solid #444',
    display: 'flex',
    justifyContent: 'center',
  },
  legend: {
    display: 'flex',
    gap: '16px',
    fontSize: '12px',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
};
